/* eslint-disable no-lone-blocks */
import { FilterOutlined } from '@ant-design/icons'
import { Button, Empty, Input, Spin, Table, Tooltip } from 'antd'
import { ColumnsType } from 'antd/es/table'
import {
  ArrowBigDownDash,
  ArrowBigUpDash,
  ArrowDownUp,
  Check,
  CheckCheck,
  Eye,
  Hourglass,
  Pencil,
  Plus,
  X,
} from 'lucide-react'
import moment from 'moment'
import { ChangeEvent, FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  ActionsCellContainer,
  ContentContainer,
  StyledRow,
  StyledSelect,
} from '../../../components/StyledComponents'
import { DATE_KEYS } from '../../../constants/dateKeys'
import { useGeneral } from '../../../contexts'
import { useFiltersV2 } from '../../../contexts/FiltersContextV2'
import useBlocktrains from '../../../hooks/blocktrain/useBlocktrains'
import { Blocktrain, BlockTrainStatusEnum, BlocktrainType } from '../../../types'
import { useDestinations } from '../AddTruckDocumentation/hooks'
import AddBlocktrainModal from './AddBlocktrainModalSupplier'
import debounce from '../../../utils/debounce'
import useInputFilter from '../../../hooks/useInputFilter'

const { AUTHORIZED, FINISHED, LOADING, TRANSIT, UNLOADING, WAITING_APPROVAL, CANCELED } =
  BlockTrainStatusEnum

const iconByStatus = {
  [LOADING]: <ArrowBigUpDash className="icon-row" />,
  [WAITING_APPROVAL]: <Hourglass className="icon-row" />,
  [AUTHORIZED]: <Check className="icon-row" />,
  [TRANSIT]: <ArrowDownUp className="icon-row" />,
  [UNLOADING]: <ArrowBigDownDash className="icon-row" />,
  [FINISHED]: <CheckCheck className="icon-row" />,
  [CANCELED]: <X className="icon-row" />,
}

const BlocktrainsSupplierView: FC = () => {
  const { t } = useTranslation()
  const { filters, handleFilterChange, handleResetFilters } = useFiltersV2()
  const { blocktrains, blocktrainsIsLoading } = useBlocktrains({ filters })
  const [addModalIsOpen, setAddModalIsOpen] = useState(false)
  const navigate = useNavigate()
  const { destinationsOptions, isLoading: isDestinationsLoading } = useDestinations()

  const { setTicketToSearch, filtersDisplayed, handleDisplayFilters, menuDisplayed } = useGeneral()

  const actionButtons = (record: BlocktrainType) => {
    const handleClick = () => {
      navigate(record.id.toString())
    }

    const shouldShowPencil =
      record.status === LOADING ||
      record.status === AUTHORIZED ||
      record.status === WAITING_APPROVAL

    return (
      <ActionsCellContainer>
        <Button className="icon-button" onClick={handleClick}>
          {shouldShowPencil ? <Pencil /> : <Eye />}
        </Button>
      </ActionsCellContainer>
    )
  }

  const orderByOptions = [
    {
      label: `${t('order_by_ID')} ${t('order_by_ascending')}`,
      value: { field: 'id', direction: 'ASC' },
    },
    {
      label: `${t('order_by_ID')} ${t('order_by_descending')}`,
      value: { field: 'id', direction: 'DESC' },
    },
  ]

  const rowClassName = (_record: unknown, index: number) =>
    index % 2 === 0 ? 'zebra-even' : 'zebra-odd'

  const columnsBlocktrains: ColumnsType<Blocktrain> = [
    {
      title: t('train_ID'),
      dataIndex: 'id',
      key: 'trainId',
      render: (id: string) => id ?? '-',
    },
    {
      title: t('origin'),
      dataIndex: 'origin',
      key: 'originCity',
      render: (origin: { city: string | null }) => origin?.city ?? '-',
    },
    {
      title: t('destination'),
      dataIndex: 'destination',
      key: 'destinationCity',
      render: (destination: { tag: string | null }) => destination?.tag ?? '-',
    },
    {
      title: t('railcars_#'),
      dataIndex: 'railCarQuantity',
      key: 'railcarsNumber',
      render: (railCarQuantity: string) => railCarQuantity ?? '-',
    },
    {
      title: t('railcars_CO'),
      dataIndex: 'trainCompany',
      key: 'trainCompany',
      render: trainCompany => trainCompany?.shortName ?? '-',
    },
    {
      title: t('ETD'),
      dataIndex: 'estimatedDateOfDeparture',
      key: 'etd',
      render: (estimatedDateOfDeparture: string) =>
        estimatedDateOfDeparture
          ? moment(estimatedDateOfDeparture).format(DATE_KEYS.FORMAT.YEAR_MONTH)
          : '-',
    },
    {
      title: t('railway_bill_#'),
      dataIndex: 'railCarWayBill',
      key: 'railCarWayBill',
      render: (sap_id: string) => sap_id ?? '-',
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'statusname',
      render: (status: BlockTrainStatusEnum) => (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {iconByStatus[status]}
          {t(`blocktrain_status.${status}`)}
        </span>
      ),
    },
    {
      title: t('actions'),
      key: 'acciones',
      align: 'center',
      className: 'column-width',
      render: actionButtons,
    },
  ]
  const handleOnCancelBlocktrainModal = () => setAddModalIsOpen(false)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterOptions = (input: string, option: any) => {
    if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
    return true
  }

  const handleCleanSearchInput = () => {
    setTicketToSearch(undefined)
  }

  const handleRailcarWayBill = debounce((e: ChangeEvent<HTMLInputElement>) => {
    handleFilterChange('railCarWayBill')(e.target.value)
  }, 500)

  useInputFilter(['railCarWayBillElement'])

  return (
    <ContentContainer>
      <StyledRow className="sticky-options">
        <Button type="primary" onClick={() => setAddModalIsOpen(true)} className="button-style">
          {t('new_blocktrain')}
          <Plus style={{ marginLeft: '0.5rem' }} />
        </Button>
      </StyledRow>
      <StyledRow className="sticky-options">
        <div
          className={`filters-container${
            filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
          }`}
        >
          <Button onClick={handleDisplayFilters} id="displayFiltersButton">
            <FilterOutlined />
          </Button>
          <div className="filters-list">
            <StyledSelect
              mode="multiple"
              allowClear
              placeholder={t('filter_by_status')}
              options={Object.values(BlockTrainStatusEnum).map(status => ({
                label: t(`blocktrain_status.${status}`),
                value: status,
              }))}
              onChange={handleFilterChange('statusName')}
              value={filters?.statusName}
              onDropdownVisibleChange={handleCleanSearchInput}
              maxTagCount="responsive"
            />
            <StyledSelect
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              placeholder={t('filter_by_destination')}
              options={destinationsOptions}
              filterOption={handleFilterOptions}
              onChange={handleFilterChange('destinationId')}
              loading={isDestinationsLoading}
              value={filters?.destinationId}
              onDropdownVisibleChange={handleCleanSearchInput}
            />
            <StyledSelect
              placeholder={t('order_by_ID')}
              options={orderByOptions.map(option => ({
                label: option.label,
                value: JSON.stringify(option.value),
              }))}
              onChange={(value: unknown) => {
                const stringValue = value as string
                const selectedValue = JSON.parse(stringValue) as {
                  field: string
                  direction: string
                }
                handleFilterChange('orderBy')({ [selectedValue.field]: selectedValue.direction })
              }}
              value={
                filters?.orderBy
                  ? JSON.stringify({
                      field: Object.keys(filters.orderBy)[0],
                      direction: Object.values(filters.orderBy)[0],
                    })
                  : undefined
              }
              maxTagCount="responsive"
              allowClear
              filterOption={handleFilterOptions}
              onDropdownVisibleChange={handleCleanSearchInput}
            />
            <div className="search-container">
              <Input
                style={{ maxWidth: '11rem' }}
                placeholder={t('filter_by_railwaybill')}
                onChange={handleRailcarWayBill}
                allowClear
                id="railCarWayBillElement"
              />
            </div>
            <Tooltip placement="bottomRight" title={t('clean_filters')}>
              <Button onClick={handleResetFilters} style={{ backgroundColor: 'unset' }}>
                {t('clean_filters')}
              </Button>
            </Tooltip>
          </div>
        </div>
      </StyledRow>
      <StyledRow className="ticket-handler">
        <div style={{ flex: 1 }} />
        {blocktrainsIsLoading ? <Spin /> : <div style={{ flex: 1 }} />}
      </StyledRow>
      <Table
        columns={columnsBlocktrains}
        dataSource={blocktrains?.blockTrains}
        rowKey="id"
        rowClassName={rowClassName}
        pagination={{
          pageSize: filters.limit as number,
          onChange: handleFilterChange('page', false),
          total: blocktrains?.total,
          showSizeChanger: false,
          current: filters.page as number,
        }}
        locale={{
          emptyText: <Empty description={t('no_data_found')} />,
        }}
        scroll={{
          x: 'max-content',
        }}
      />
      <AddBlocktrainModal visible={addModalIsOpen} onCancel={handleOnCancelBlocktrainModal} />
    </ContentContainer>
  )
}

export default BlocktrainsSupplierView
